import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const {FiShield, FiUsers, FiAlertTriangle, FiCheck, FiX, FiBriefcase, FiGavel, FiEye, FiUserX} = FiIcons;

const EPLIPage = () => {
  const threats = [
    {
      title: "Discrimination",
      description: "A former employee files a lawsuit alleging they were fired because of age, race, religion, sex or disability.",
      icon: FiUsers
    },
    {
      title: "Wrongful termination", 
      description: "A former employee alleges they were fired in retaliation for a whistleblower claim.",
      icon: FiUserX
    },
    {
      title: "Harassment",
      description: "An employee alleges derogatory verbal or physical behavior like jokes, slurs, comments or offensive pictures.",
      icon: FiAlertTriangle
    },
    {
      title: "Privacy invasion",
      description: "Confidential employee health information is leaked and an employee sues.",
      icon: FiEye
    },
    {
      title: "Failure to hire or promote",
      description: "A well-qualified applicant sues after they weren't hired claiming it was because of their disability.",
      icon: FiBriefcase
    },
    {
      title: "ADA violation",
      description: "A visually impaired customer sues because your website does not accommodate screen reader software.",
      icon: FiGavel
    }
  ];

  const industries = [
    {
      name: "Construction",
      description: "Protect against workplace discrimination and harassment claims in construction environments."
    },
    {
      name: "Food & Beverage",
      description: "Coverage for restaurants, cafes, and food service businesses against employee lawsuits."
    },
    {
      name: "Retail & E-Commerce",
      description: "Protection for retail stores and online businesses from discrimination and ADA claims."
    },
    {
      name: "Cleaning Services",
      description: "Coverage for cleaning companies against wrongful termination and harassment claims."
    },
    {
      name: "Professional Services",
      description: "Protect consulting, legal, and other professional service firms from EPLI claims."
    },
    {
      name: "Manufacturing",
      description: "Coverage for manufacturing businesses against workplace discrimination lawsuits."
    }
  ];

  const coveredClaims = [
    "Sexual harassment",
    "Wrongful termination", 
    "Discrimination",
    "Breach of employment contract",
    "Negligent evaluation",
    "Failure to hire or promote",
    "Wrongful discipline",
    "Deprivation of career opportunity",
    "Wrongful infliction of emotional distress",
    "Mismanagement of benefits",
    "Wrongful discipline or demotion",
    "Privacy invasion"
  ];

  const notCovered = [
    "Physical injuries (bodily injury)",
    "Property damage",
    "Intentional or criminal acts",
    "Contractors or 1099 employees",
    "Wage and hour claims",
    "Equity compensation",
    "Punitive damages",
    "Civil or criminal fines"
  ];

  const costFactors = [
    "The type of work you do",
    "Number of employees",
    "Your claims history", 
    "Your work experience",
    "The limits you choose",
    "The state where you work"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2084&q=80)'}}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8}}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Employment Practices Liability Insurance (EPLI)
            </motion.h1>
            <motion.p
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Coverage for employee lawsuits around discrimination, harassment and wrongful termination. Protect your business from costly legal battles.
            </motion.p>
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.4}}
            >
              <CTAButton 
                text="Get Your EPLI Quote" 
                size="xl" 
                className="mb-8"
              />
            </motion.div>
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.8, delay: 0.6}}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Selection */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Choose Your Primary Industry to Get Started
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-blue-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiBriefcase} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Construction</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-green-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Food & Beverage</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-purple-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Retail & E-Commerce</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              className="text-center p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-yellow-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiGavel} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Cleaning</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="text-center p-6 bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-gray-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Other Industries</h3>
              <CTAButton text="Get a Quote" size="sm" className="w-full" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Threats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Get Coverage That Could Help with Legal Costs If Your Business Is Accused Of:
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {threats.map((threat, index) => (
              <motion.div
                key={threat.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={threat.icon} className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{threat.title}</h3>
                <p className="text-gray-600">{threat.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.8}}
            className="text-center mt-12"
          >
            <CTAButton text="Get Instant Quote" />
          </motion.div>
        </div>
      </section>

      {/* What is EPLI */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is EPLI Insurance?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Employment practices liability insurance (EPLI), an add-on to general liability insurance, can help cover costs if your business is sued by a customer or an employee for things such as sexual harassment, discrimination or wrongful termination.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                An EPLI policy could help cover your legal fees if an employee (or several employees) accuses you of unfairly terminating them, or if a customer accuses you of violating their rights.
              </p>
              <p className="text-lg text-gray-700">
                Employment practices insurance can also help pay for legal expenses associated with Americans with Disabilities Act (ADA) compliance complaints.
              </p>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Small Businesses Need EPLI</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700">Small businesses may be more vulnerable to harassment or discrimination claims because they don't usually have a human resource department, legal staff or employee handbook.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700">EPLI can cover business owners, managers and employees as an optional add-on to your NEXT general liability policy.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Can EPLI Insurance Cover?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              If your small business is sued by a customer, employee or former employee, employment liability coverage could help cover your legal defense costs, and the costs of a settlement or judgement.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* What's Covered */}
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What's Covered</h3>
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <p className="text-gray-700 mb-6">
                  Some of the more common kinds of claims covered under an EPLI policy include:
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {coveredClaims.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* What's Not Covered */}
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What's Not Covered</h3>
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <p className="text-gray-700 mb-4">
                  EPLI can help with legal costs around some lawsuits that affect your business, but it does not cover all types of incidents, including:
                </p>
                <ul className="space-y-3">
                  {notCovered.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiX} className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> EPLI will not cover claims if you don't have an active policy when you receive notice of a claim — even if you had EPLI when the incident occurred.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Any Business Can Be Subject to Employee Lawsuits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Any business can be subject to a lawsuit from a former or current employee or a customer who feels they've been discriminated against.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-gray-50 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{industry.name}</h3>
                <p className="text-gray-600 text-sm">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost & Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Cost Section */}
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How Much Does EPLI Insurance Cost?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                What you'll spend on EPLI coverage depends on a variety of factors, such as:
              </p>
              <ul className="space-y-3 mb-6">
                {costFactors.map((factor, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{factor}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <p className="text-blue-800">
                  <strong>Save up to 10%</strong> when you bundle EPLI and general liability insurance with an additional policy, such as workers' compensation insurance.
                </p>
              </div>
            </motion.div>

            {/* Comparison Section */}
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                EPLI vs. Workers' Compensation
              </h3>
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                  <h4 className="text-lg font-bold text-green-800 mb-3">Workers' Compensation</h4>
                  <p className="text-gray-700">
                    Workers' comp insurance can help protect your employees from work-related injuries and illnesses such as medical costs, job retraining, physical disabilities and more.
                  </p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                  <h4 className="text-lg font-bold text-blue-800 mb-3">EPLI Insurance</h4>
                  <p className="text-gray-700">
                    EPLI can help you pay legal fees for professional discrimination claims against your business. It can help to protect your business from employee accusations of unfair treatment — which is not covered by workers' comp.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Get Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How to Get Employment Practices Liability Insurance with NEXT
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="bg-gradient-to-br from-primary-50 to-blue-50 p-8 rounded-lg border border-primary-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    1
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Start Free Quote</h3>
                  <p className="text-gray-600 text-sm">Answer a few questions about your business and employee count</p>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    2
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Add EPLI Coverage</h3>
                  <p className="text-gray-600 text-sm">Add EPLI as an optional enhancement to your general liability policy</p>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    3
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Buy Online</h3>
                  <p className="text-gray-600 text-sm">Get insured immediately after payment with instant certificate access</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg text-gray-700 mb-6">
                  NEXT has EPLI as an add-on for general liability insurance. You can buy NEXT EPLI insurance with general liability coverage 100% online in about 10 minutes. You're insured immediately after payment, and you'll have access to your certificate of insurance at no extra cost.
                </p>
                <CTAButton text="Start a Free EPLI Quote with NEXT" size="lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Protect Your Business from Employment Lawsuits
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-xl mb-8"
          >
            Don't let discrimination, harassment, or wrongful termination claims devastate your business. Get comprehensive EPLI coverage today.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.4}}
          >
            <CTAButton 
              text="Get Your EPLI Quote Now" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EPLIPage;